import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  Input,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  useChatAvailability,
  useChatTemplates,
  useSendChatMessage,
} from "../../../../lib/hooks/useChat";
import { unavailableMessage } from "../data/chatInbox";

const { Text } = Typography;

/**
 * Composer to start a brand-new WhatsApp conversation from a dossier that has no
 * conversation yet. Since there is no open 24h window, WhatsApp requires a
 * template to initiate the conversation, so only the template picker is shown.
 */
export const ChatDossierComposer = ({ dossierId }: { dossierId: string }) => {
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [templateVariables, setTemplateVariables] = useState<
    Record<string, string>
  >({});

  const availabilityQuery = useChatAvailability(dossierId);
  const templatesQuery = useChatTemplates(true);
  const sendMutation = useSendChatMessage();

  const selectedTemplate = useMemo(
    () => templatesQuery.data?.find((template) => template.id === templateId),
    [templatesQuery.data, templateId],
  );

  if (availabilityQuery.data && !availabilityQuery.data.canSendChat) {
    return (
      <Alert
        type="warning"
        showIcon
        message={unavailableMessage(availabilityQuery.data.unavailableReason)}
      />
    );
  }

  const send = () => {
    if (!selectedTemplate) {
      return;
    }

    sendMutation.mutate(
      {
        dossierId,
        input: { templateId: selectedTemplate.id, templateVariables },
      },
      {
        onSuccess: () => {
          setTemplateId(undefined);
          setTemplateVariables({});
          message.success("Bericht verstuurd");
        },
        onError: () =>
          message.error(
            "Versturen mislukt. Buiten het 24-uursvenster kan alleen een template worden verstuurd.",
          ),
      },
    );
  };

  return (
    <Flex vertical gap={8}>
      <Text strong>Nieuw WhatsApp-bericht sturen</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>
        Er is nog geen gesprek. Start met een template.
      </Text>

      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Select
          size="small"
          style={{ width: "100%" }}
          placeholder="Kies een template"
          value={templateId}
          onChange={(value) => {
            setTemplateId(value);
            setTemplateVariables({});
          }}
          options={templatesQuery.data?.map((template) => ({
            value: template.id,
            label: `${template.name} (${template.language})`,
          }))}
          loading={templatesQuery.isLoading}
        />

        {selectedTemplate?.variables.map((variable) => (
          <Input
            key={variable}
            size="small"
            placeholder={variable}
            value={templateVariables[variable] ?? ""}
            onChange={(event) =>
              setTemplateVariables((current) => ({
                ...current,
                [variable]: event.target.value,
              }))
            }
          />
        ))}

        {selectedTemplate ? (
          <Button
            type="primary"
            size="small"
            loading={sendMutation.isPending}
            onClick={send}
          >
            Template versturen
          </Button>
        ) : null}
      </Space>
    </Flex>
  );
};
